// ─────────────────────────────────────────────────────────────────────────
// RoleFormPermissionsEditor — Role Configure → Form tab.
//
// Unlike ModuleFieldConfigEditor (List View / Filter tabs, an independently
// seeded flat field list), this tab's structure comes straight from Form
// Groups: GET /admin/role-form-permissions derives the group/field tree from
// the module's current Form Groups config and overlays this role's saved
// grants. Form Groups stays the single source of truth — new/renamed/removed
// groups and fields sync here automatically, and a role can only ever narrow
// what's globally enabled, never widen it (enforced again server-side on save
// via ClampFormPermissionFieldsToGlobal).
// ─────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useState } from 'react';
import { Button, Empty, Space, Spin, Switch, Tabs, Tooltip, Typography, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { getAvailableModules, getRoleFormPermissions, saveRoleFormPermissions } from '../../../../services/adminApi';

// Stable reference so the load effect below doesn't re-run every render.
const EMPTY_SCOPE = Object.freeze({ clientId: '', region: '' });
import { normalizeModuleOptions } from '../../../../utils/moduleOptions';
import '../../../../styles/UserSettingsSection.css';
import '../../../../styles/FormGroupsSection.css';

const { Text } = Typography;

function withGroupEnabled(group, enabled) {
  return {
    ...group,
    enabled,
    fields: group.fields.map((f) => ({ ...f })),
  };
}

function withGroupEditable(group, editable) {
  return {
    ...group,
    editable,
    fields: group.fields.map((f) => ({ ...f })),
  };
}

export default function RoleFormPermissionsEditor({ role }) {
  const [moduleTabs, setModuleTabs]     = useState([]);
  const [activeModule, setActiveModule] = useState('');
  const [groups, setGroups]             = useState([]);
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);
  // Role form permissions are configured against the module's DEFAULT Form
  // Groups config. Client-scoped overrides are not selectable here: a role's
  // field permissions are role-wide, and scoping them per client made the
  // screen show a different field list than the one being saved. Client-scoped
  // FORM structure is still configured in Admin → Form Groups.
  const scope = EMPTY_SCOPE;

  const loadModules = useCallback(async () => {
    const raw = await getAvailableModules().catch(() => []);
    return normalizeModuleOptions(raw).map(({ value, label }) => ({ key: value, label }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      loadModules().then((tabs) => {
        if (cancelled) return;
        setModuleTabs(tabs);
        setActiveModule((current) => (tabs.some((t) => t.key === current) ? current : tabs[0]?.key ?? ''));
      });
    });
    return () => { cancelled = true; };
  }, [loadModules]);

  const loadGroups = useCallback(async (module, currentScope) => {
    if (!role || !module) { setGroups([]); return; }
    setLoading(true);
    try {
      const data = await getRoleFormPermissions(role, module, currentScope);
      setGroups(Array.isArray(data) ? data : []);
    } catch {
      message.error('Failed to load form permissions');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    queueMicrotask(() => { loadGroups(activeModule, scope); });
  }, [activeModule, scope, loadGroups]);

  function toggleGroup(groupName, enabled) {
    setGroups((prev) => prev.map((g) => (g.name === groupName ? withGroupEnabled(g, enabled) : g)));
  }

  function toggleGroupEditable(groupName, editable) {
    setGroups((prev) => prev.map((g) => (g.name === groupName ? withGroupEditable(g, editable) : g)));
  }

  function toggleField(groupName, fieldKey, enabled) {
    setGroups((prev) => prev.map((g) => (
      g.name !== groupName ? g : {
        ...g,
        fields: g.fields.map((f) => (f.field === fieldKey ? { ...f, enabled } : f)),
      }
    )));
  }

  function toggleFieldEditable(groupName, fieldKey, editable) {
    setGroups((prev) => prev.map((g) => (
      g.name !== groupName ? g : {
        ...g,
        fields: g.fields.map((f) => (f.field === fieldKey ? { ...f, editable } : f)),
      }
    )));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await saveRoleFormPermissions(role, activeModule, groups);
      message.success('Saved');
    } catch {
      message.error('Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Spin spinning={loading}>
      <Tabs
        size="small"
        activeKey={activeModule}
        onChange={setActiveModule}
        items={moduleTabs.map((t) => ({ key: t.key, label: t.label }))}
      />
      <div className="fg-expanded-fields" style={{ marginTop: 8 }}>
        {groups.length === 0 && !loading && <Empty description="No form groups configured for this module." />}
        {groups.map((group) => (
          <div key={group.name} className="fg-field-card" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
            <div className="fg-field-card-inner" style={{ padding: '8px 12px' }}>
              <span className="uss-field-name" style={{ flex: 1 }}>
                <strong>{group.label}</strong>
                {group.locked && (
                  <Tooltip title="Every field in this group is disabled globally in Form Groups">
                    <LockOutlined style={{ marginLeft: 6, color: 'var(--color-text-muted)' }} />
                  </Tooltip>
                )}
              </span>
              <Space size={16}>
                <span className="uss-toggle-pair">
                  <Text type="secondary" style={{ fontSize: 11, marginRight: 6 }}>Show</Text>
                  <Switch
                    size="small"
                    checked={group.enabled}
                    disabled={group.locked}
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    onChange={(v) => toggleGroup(group.name, v)}
                  />
                </span>
                <span className="uss-toggle-pair">
                  <Text type="secondary" style={{ fontSize: 11, marginRight: 6 }}>Editable</Text>
                  <Switch
                    size="small"
                    checked={group.editable !== false}
                    disabled={group.locked || !group.enabled}
                    checkedChildren="Yes"
                    unCheckedChildren="No"
                    onChange={(v) => toggleGroupEditable(group.name, v)}
                  />
                </span>
              </Space>
            </div>
            <div className="fg-expanded-fields" style={{ padding: '4px 12px 10px 28px', opacity: group.enabled ? 1 : 0.5 }}>
              {group.fields.map((field) => (
                <div key={field.field} className="fg-field-row" style={{ justifyContent: 'space-between' }}>
                  <span className="fg-field-row-info">
                    <span className="fg-field-row-label">{field.label}</span>
                    <span className="fg-field-row-key">{field.field}</span>
                    {field.locked && (
                      <Tooltip title="Disabled globally in Form Groups — a role can never re-enable it">
                        <LockOutlined style={{ color: 'var(--color-text-muted)' }} />
                      </Tooltip>
                    )}
                  </span>
                  <Space size={16}>
                    <span className="uss-toggle-pair">
                      <Text type="secondary" style={{ fontSize: 11, marginRight: 6 }}>Show</Text>
                      <Switch
                        size="small"
                        checked={field.enabled}
                        disabled={field.locked || !group.enabled}
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        onChange={(v) => toggleField(group.name, field.field, v)}
                      />
                    </span>
                    <span className="uss-toggle-pair">
                      <Text type="secondary" style={{ fontSize: 11, marginRight: 6 }}>Editable</Text>
                      <Switch
                        size="small"
                        checked={field.editable !== false}
                        disabled={field.locked || !group.enabled || !field.enabled || group.editable === false}
                        checkedChildren="Yes"
                        unCheckedChildren="No"
                        onChange={(v) => toggleFieldEditable(group.name, field.field, v)}
                      />
                    </span>
                  </Space>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {groups.length > 0 && (
        <div className="uss-modal-footer">
          <Button type="primary" size="small" loading={saving} onClick={handleSave}>
            Save {moduleTabs.find((t) => t.key === activeModule)?.label}
          </Button>
        </div>
      )}
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
        Groups and fields are read from Form Groups. <b>Show</b> hides a group/field from the
        add &amp; edit form; <b>Editable</b> keeps it visible but read-only (disabled). Everything
        is shown and editable by default. A role can only ever narrow what Form Groups already
        enables — never re-enable something disabled globally.
      </Text>
    </Spin>
  );
}
